/**
 * Convert any error from an axios/fetch call into a safe, human-readable string.
 *
 * FastAPI's `HTTPException` returns `{ "detail": "<string>" }`, but Pydantic
 * validation errors return `{ "detail": [ { msg, type, loc, ... } ] }`. If a
 * caller blindly passes the array straight to a React component (e.g. sonner
 * `toast.error`) it throws "Objects are not valid as a React child" and tears
 * down the whole React tree — leaving the user staring at a white screen.
 *
 * Use this helper everywhere we surface API errors to the UI.
 */
export function getApiErrorMessage(error, fallback = 'Something went wrong') {
  if (!error) return fallback;

  // Network errors / no response
  const data = error.response?.data;
  if (data === undefined || data === null) {
    return error.message || fallback;
  }

  // Plain string body
  if (typeof data === 'string') return data;

  const detail = data.detail;

  // FastAPI HTTPException with string detail
  if (typeof detail === 'string') return detail;

  // FastAPI/Pydantic validation errors – detail is an array of objects
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (!item || typeof item !== 'object') return null;
        // Pydantic field path: ("body", "email") -> "email"
        const loc = Array.isArray(item.loc)
          ? item.loc.filter((p) => p !== 'body').join('.')
          : '';
        const msg = item.msg || item.message || 'Invalid value';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (messages.length) return messages.join('; ');
  }

  // Some endpoints return { message: "..." }
  if (typeof data.message === 'string') return data.message;

  return fallback;
}
